import React, { useState, useEffect, useContext } from "react";
import { Formik, Form } from "formik";
import { Box, Button, Grid, HStack, VStack, useToast } from "@chakra-ui/react";
import { SuppliersContext } from "../../Context/SuppliersContext";
import * as Yup from "yup";
import CustomField from "../../Common/Form/CustomField";
import { useHistory } from "react-router-dom";
import { arrayToOption } from "../../../Utils/formatUtils";
import ProductList from "./ProductList";
import ContentCard from "../../Common/Layout/ContentCard";
import LabeledText from "../../Common/Layout/LabeledText";
import useFetch from "../../../hooks/useFetch";
import { Prompt } from "react-router";
import ConfirmationSwal from "../../../Utils/confirmationSwal";

const PurchaseForm = ({ purchase }) => {
  const { suppliers, isLoadingSuppliers } = useContext(SuppliersContext);
  const [products, setProducts] = useState([]);
  const [suppliersOptions, setSuppliersOptions] = useState([]);
  const [isSubmitting, setIsSubmiting] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const { post } = useFetch();
  const history = useHistory();
  const toast = useToast();

  useEffect(() => {
    setSuppliersOptions(arrayToOption(suppliers, "id", "businessName"));
  }, [isLoadingSuppliers]);

  const PurchaseSchema = Yup.object().shape({
    supplierId: Yup.object().nullable().required("Ingrese un proveedor"),
  });

  const submitHandler = async (form, isRedirecting) => {
    await form.submitForm();
    if (Object.keys(await form.validateForm()).length !== 0) return false;
    const value = form.values;

    if (products.length === 0) {
      toast({
        title: "Se ha producido un error.",
        description: "Ingrese al menos un artículo.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });

      return false;
    }
    setIsSubmiting(true);

    let data = {
      supplierId: value.supplierId.value,
      purchasedAt: value.purchasedAt,
      comments: value.comments,
      products: products,
    };
    setIsEdited(false);
    await post("/suppliers/purchases", data)
      .then((res) => {
        toast({
          title: "Compra creada con éxito.",
          description: "Se cargó la compra correctamente.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });

        if (isRedirecting) {
          history.push("/compras");
        } else {
          history.replace("/compras");
          history.push(`/compras/${res.id}`);
        }
      })
      .catch((e) => {
        toast({
          title: "Se ha producido un error.",
          description:
            e?.message || "Contacte con el administrador del sistema.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => {
        setIsSubmiting(false);
      });
  };

  const handleIsEdited = () => {
    setIsEdited(true);
  };

  const handleBlockedNavigation = (nextLocation) => {
    if (isEdited && products.length > 0) {
      setIsEdited(false);
      ConfirmationSwal.fire({
        title: "Parece que hay cambios no guardados",
        text: "Desea salir de la página?",
        icon: "question",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.isConfirmed) {
          history.push(nextLocation.pathname);
        }
      });
      return false;
    }
    return true;
  };

  return (
    <>
      <Box pb="1rem">
        <Formik
          initialValues={purchase}
          onSubmit={() => {}}
          validationSchema={PurchaseSchema}
        >
          {(props) => (
            <Form>
              <ContentCard w={["auto"]}>
                <Grid
                  templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
                  gap={6}
                >
                  <Box>
                    <CustomField
                      name="supplierId"
                      title="Proveedor"
                      placeholder="Ingrese un proveedor"
                      inputType="select"
                      options={suppliersOptions}
                      props={props}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                    <VStack align="left" w="100%" pt="1rem">
                      <LabeledText
                        label={"CUIT"}
                        text={
                          props.values.supplierId &&
                          suppliers.find(
                            (sup) => sup.id == props.values.supplierId.value
                          )?.cuit
                        }
                      ></LabeledText>
                      <LabeledText
                        label={"Dirección"}
                        text={
                          props.values.supplierId &&
                          suppliers.find(
                            (sup) => sup.id == props.values.supplierId.value
                          )?.address
                        }
                      ></LabeledText>
                    </VStack>
                  </Box>

                  <VStack>
                    <CustomField
                      name="purchasedAt"
                      title="Fecha de compra:"
                      type="date"
                      props={props}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                    <CustomField
                      name="comments"
                      title="Observación: (opcional)"
                      placeholder="Ingrese una observación"
                      type="text"
                      inputType="textArea"
                      props={props}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                  </VStack>
                </Grid>
              </ContentCard>

              <ContentCard w={["auto"]}>
                <ProductList
                  purchase={purchase}
                  setNewProductList={setProducts}
                  setIsEdited={handleIsEdited}
                />
              </ContentCard>
              <HStack
                justify={["center", "flex-end"]}
                mt="2rem"
                wrap={"wrap"}
                gap="0.5rem"
              >
                <Button variant="ghost" onClick={() => history.goBack()}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, true)}
                >
                  Guardar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, false)}
                >
                  Guardar y seguir editando
                </Button>
              </HStack>
            </Form>
          )}
        </Formik>
      </Box>
      <Prompt when={isEdited} message={(e) => handleBlockedNavigation(e)} />
    </>
  );
};

export default PurchaseForm;
